import type { LinkedEntityScope } from './workflowDefinition'
import type { LinkedEntityType, WorkflowRunStatus } from './workflowRun'

export type ChainDefinitionCategory = 'Analysis' | 'Delivery' | 'Maintenance'

export const CHAIN_DEFINITION_CATEGORIES: ChainDefinitionCategory[] = ['Analysis', 'Delivery', 'Maintenance']

export const CHAIN_CATEGORY_DOT_COLORS: Record<ChainDefinitionCategory | 'Uncategorized', string> = {
  Analysis: 'bg-[rgba(38,132,255,1)]',
  Delivery: 'bg-[rgba(54,179,126,1)]',
  Maintenance: 'bg-[rgba(255,153,31,1)]',
  Uncategorized: 'bg-[rgba(128,133,155,1)]',
}

export type TriggerOn = 'success' | 'always' | 'manual'

export const TRIGGER_ON_VALUES: TriggerOn[] = ['success', 'always', 'manual']

export const TRIGGER_ON_LABELS: Record<TriggerOn, string> = {
  success: 'On success',
  always: 'Always',
  manual: 'Manual',
}

export const triggerOnLabel = (value: string): string =>
  TRIGGER_ON_LABELS[value.toLowerCase() as TriggerOn] ?? value

export interface ChainYamlSnapshot {
  id: string
  chainDefinitionId: string
  yamlContent: string
  contentHash: string
  version: string
  createdAt: string
}

export interface ChainDefinition {
  id: string
  projectId: string | null
  name: string
  displayName: string
  description: string | null
  version: string
  yamlContent: string
  contentHash: string
  isActive: boolean
  isSystem: boolean
  linkedEntityScope?: LinkedEntityScope
  permittedRoleIds: string[]
  category?: ChainDefinitionCategory
  currentYamlSnapshotId: string | null
}

export interface ChainDefinitionSummary {
  id: string
  projectId: string | null
  name: string
  displayName: string
  description: string | null
  version: string
  isActive: boolean
  isSystem: boolean
  yamlContent: string
  linkedEntityScope?: LinkedEntityScope
  permittedRoleIds: string[]
  category?: ChainDefinitionCategory
}

export interface ChainDefinitionFilters {
  search: string
  status: 'all' | 'active' | 'inactive'
  type: 'all' | 'system' | 'custom'
  category: 'all' | ChainDefinitionCategory
  linkedEntityScope?: LinkedEntityScope
}

export interface CreateChainDefinitionRequest {
  name: string
  displayName: string
  description?: string
  version: string
  yamlContent: string
  isActive: boolean
  linkedEntityScope?: LinkedEntityScope
  permittedRoleIds: string[]
  category?: ChainDefinitionCategory
}

export interface UpdateChainDefinitionRequest {
  name: string
  displayName: string
  description?: string
  version: string
  yamlContent: string
  isActive: boolean
  linkedEntityScope?: LinkedEntityScope
  permittedRoleIds: string[]
  category?: ChainDefinitionCategory
}

// Parsed representation of the chain YAML body, edited by the builder view.
export interface ChainInputModel {
  name: string
  type: string
  required?: boolean
  default?: string
  description?: string
  options?: string[]
  multiSelect?: boolean
  contextType?: string
  connectorType?: string
}

export interface ChainLinkModel {
  name: string
  workflow: string
  triggerOn: TriggerOn
  inputs: [string, string][]
}

export interface ChainModel {
  inputs: ChainInputModel[]
  links: ChainLinkModel[]
}

export interface StartChainRequest {
  inputValuesJson?: string
  variableGroupIds?: string[]
  linkedEntityType?: LinkedEntityType
  linkedEntityId?: string
}

export interface StartChainResponse {
  chainRunId: string
  firstWorkflowRunId: string
  chatId: string
}

export interface ChainRunFailureResponse {
  chainRunId: string | null
  errors: Array<{ field: string; errorMessage: string }>
}

export type ChainRunStatus =
  | 'Pending'
  | 'Running'
  | 'AwaitingManualStart'
  | 'AwaitingInputs'
  | 'Completed'
  | 'Failed'
  | 'Cancelled'

export type ChainRunLinkStatus =
  | 'Pending'
  | 'Triggered'
  | 'Completed'
  | 'Failed'
  | 'Skipped'
  | 'Cancelled'

export type ChainRunTriggerSource = 'User' | 'AzureDevOpsIngestion'

export interface ChainRunSummary {
  id: string
  chainDefinitionId: string
  chainName: string
  chainDisplayName: string
  definitionVersion: string
  status: ChainRunStatus
  triggeredBy: string
  triggeredByEmail: string | null
  triggeredByUserId: string | null
  triggerSource: ChainRunTriggerSource
  linkedEntityType: LinkedEntityType | null
  linkedEntityId: string | null
  linkedEntityNumber: number | null
  linkedEntityAvailable?: boolean | null
  linkedEntityStatus?: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  totalLinks: number
  completedLinks: number
  currentLinkName: string | null
  errorSummary: string | null
  chatId: string | null
}

export interface ChainRunLinkRun {
  workflowRunId: string
  status: WorkflowRunStatus
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  chatId: string | null
  inputValues: Record<string, unknown> | null
}

export interface ChainRunLink {
  id: string
  position: number
  linkName: string
  workflowName: string
  workflowDisplayName: string
  triggerOn: string
  status: ChainRunLinkStatus
  inputMappings: Record<string, string>
  suppliedInputs: Record<string, string>
  missingInputs: string[]
  workflowRunId: string | null
  run: ChainRunLinkRun | null
}

export interface ChainRunDetail extends ChainRunSummary {
  inputValues: Record<string, string>
  links: ChainRunLink[]
}

export interface ChainRunFilters {
  search: string
  status: ChainRunStatus | ''
  chainDefinitionId: string
  linkedEntityId?: string
  triggerSource?: ChainRunTriggerSource | ''
}

export interface AdvanceChainRunResponse {
  chainRunId: string
  workflowRunId: string
}


const ACTIVE_CHAIN_RUN_STATUSES: Set<ChainRunStatus> = new Set([
  'Pending',
  'Running',
  'AwaitingManualStart',
  'AwaitingInputs',
])

export function isActiveChainRunStatus(status: ChainRunStatus): boolean {
  return ACTIVE_CHAIN_RUN_STATUSES.has(status)
}
